'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Stack, Card, CardContent, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { color, spacing } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import SectionWrapper from '@/components/common/SectionWrapper';
import StarRating from '@/components/common/StarRating';
import NavigationControls from '@/components/common/NavigationControls';

interface Review {
    id: string;
    rating: number;
    text: string;
    author: string;
    title: string;
}

interface ReviewsProps {
    autoPlay?: boolean;
    autoPlayInterval?: number;
    enableSwipe?: boolean;
}

const reviewsData: Review[] = [
    {
        id: '1',
        rating: 5,
        text: 'Working with your company has been an exceptional experience. Your professionalism, attention to detail, and consistent communication make every shipment seamless and stress-free. We value the trust you’ve built and look forward to continuing our long-term partnership.',
        author: 'Majdi Bin Said Al Farsi Trading',
        title: 'Oman'
    },
    {
        id: '2',
        rating: 4.5,
        text: 'The best shipping company we’ve worked with. The team is incredibly helpful and professional, and it truly feels like family. We trust them completely with our shipments.',
        author: 'Jawharat Al Hayat General Trading Co',
        title: 'Jordan'
    },
    {
        id: '3',
        rating: 4.5,
        text: "Excellent service with consistent communication. Our vehicles always arrive safely and on time. A trustworthy team that makes shipping smooth and reliable.",
        author: 'Royal World Engineering and Trading',
        title: 'Oman'
    },
    {
        id: '4',
        rating: 4.5,
        text: "We’ve worked with W8 Shipping for many years and are extremely satisfied with their service. Any issues are handled quickly and professionally. Shipping cars and motorcycles is always smooth and efficient.",
        author: 'MB AA Transport',
        title: 'Lithuania'
    },
    {
        id: '5',
        rating: 4.5,
        text: "We’ve had a great experience working with W8 Shipping as a long-term partner. Customer service has been strong on both the USA and Oman sides, with reliable loading and prompt communication whenever issues arise. We value the team’s commitment and continued collaboration.",
        author: 'The Green Way for Trading and \nServices LLC',
        title: 'Oman'
    }
];

const SlidingTrack = styled(Box)({
    display: 'flex',
    transition: 'transform 500ms ease-in-out',
    willChange: 'transform'
});

export default function Reviews({
    autoPlay = true,
    autoPlayInterval = 5000,
    enableSwipe = true
}: ReviewsProps) {
    const t = useTranslations('HomePage.reviews');
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);

    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const touchEndRef = useRef<{ x: number; y: number } | null>(null);
    const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

    const reviewsPerSlide = isMdUp ? 3 : isSmUp ? 2 : 1;
    const totalSlides = Math.ceil(reviewsData.length / reviewsPerSlide);
    const gap = isMdUp ? spacing[32] : isSmUp ? spacing[24] : spacing[0];
    const cardBasis = `calc((100% - ${reviewsPerSlide - 1} * ${gap}) / ${reviewsPerSlide})`;

    const goToSlide = useCallback(
        (slideIndex: number) => {
            const newSlideIndex = (slideIndex + totalSlides) % totalSlides;
            setCurrentSlide(newSlideIndex);
        },
        [totalSlides]
    );

    const nextSlide = useCallback(() => {
        goToSlide(currentSlide + 1);
    }, [currentSlide, goToSlide]);

    const prevSlide = useCallback(() => {
        goToSlide(currentSlide - 1);
    }, [currentSlide, goToSlide]);

    useEffect(() => {
        if (isAutoPlaying && autoPlay) {
            autoPlayRef.current = setInterval(nextSlide, autoPlayInterval);
        }
        return () => {
            if (autoPlayRef.current) {
                clearInterval(autoPlayRef.current);
            }
        };
    }, [isAutoPlaying, autoPlay, autoPlayInterval, nextSlide]);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [isMdUp, isSmUp]);

    useEffect(() => {
        if (currentSlide >= totalSlides) {
            setCurrentSlide(0);
        }
    }, [totalSlides, currentSlide]);

    const handleMouseEnter = () => setIsAutoPlaying(false);
    const handleMouseLeave = () => setIsAutoPlaying(autoPlay);

    const handleTouchStart = (e: React.TouchEvent) => {
        if (!enableSwipe) return;
        touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        touchEndRef.current = null; // Reset on new touch
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!enableSwipe || !touchStartRef.current) return;
        touchEndRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };

    const handleTouchEnd = () => {
        if (!enableSwipe || !touchStartRef.current || !touchEndRef.current) return;
        const deltaX = touchEndRef.current.x - touchStartRef.current.x;
        const deltaY = touchEndRef.current.y - touchStartRef.current.y;

        if (Math.abs(deltaX) > 30 && Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX < 0) {
                nextSlide();
            } else {
                prevSlide();
            }
        }
        touchStartRef.current = null;
    };


    return (
        <SectionWrapper
            backgroundColor={color.white[0]}
            contentAlignItems="center"
            contentJustifyContent="center"
        >
            <Stack
                spacing={{ xs: spacing[48], sm: spacing[64], md: spacing[80] }}
                alignItems="center"
                width="100%"
                sx={{
                    maxWidth: '100%'
                }}
            >
                <Typography
                    variant="heading5bold"
                    color={color.gray[80]}
                    textAlign="center"
                    component="h2"
                    sx={{
                        maxWidth: { md: '100%', sm: '100%', xs: '240px' },
                    }}
                >
                    {t('title')}
                </Typography>

                <Stack
                    spacing={spacing[32]}
                    width="100%"
                    sx={{
                        position: 'relative'
                    }}
                >
                    <Box
                        ref={containerRef}
                        sx={{ overflow: 'hidden', width: '100%' }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <SlidingTrack
                            sx={{
                                width: containerWidth ? `${totalSlides * containerWidth}px` : `${totalSlides * 100}%`,
                                transform: containerWidth ? `translateX(-${currentSlide * containerWidth}px)` : `translateX(-${currentSlide * (100 / totalSlides)}%)`
                            }}
                        >
                            {Array.from({ length: totalSlides }).map((_, slideIndex) => {
                                const slideReviews = reviewsData.slice(
                                    slideIndex * reviewsPerSlide,
                                    (slideIndex + 1) * reviewsPerSlide
                                );

                                return (
                                    <Stack
                                        key={slideIndex}
                                        direction="row"
                                        spacing={{ xs: spacing[0], sm: spacing[24], md: spacing[32] }}
                                        sx={{
                                            flex: containerWidth ? `0 0 ${containerWidth}px` : `0 0 ${100 / totalSlides}%`,
                                            width: containerWidth ? `${containerWidth}px` : `${100 / totalSlides}%`,
                                            px: 0
                                        }}
                                    >
                                        {slideReviews.map((review) => (
                                            <Card
                                                key={review.id}
                                                sx={{
                                                    backgroundColor: color.gray[10],
                                                    borderRadius: `${spacing[16]} ${spacing[0]} ${spacing[16]} ${spacing[0]}`,
                                                    boxShadow: 'none',
                                                    flex: `0 0 ${cardBasis}`,
                                                    minWidth: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'space-between',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    padding: { md: spacing[24], sm: spacing[20], xs: spacing[16] },
                                                    boxSizing: 'border-box',
                                                    minHeight: { md: '443px', xs: '340px' },
                                                    height: '100%'
                                                }}
                                            >
                                                <CardContent
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: spacing[16],
                                                        padding: 0,
                                                        height: '100%'
                                                    }}
                                                >
                                                    <StarRating rating={review.rating} size={24} />
                                                    <Typography
                                                        variant="heading1bold"
                                                        color={color.gray[80]}
                                                        sx={{ lineHeight: 1.25, paddingBottom: { md: spacing[40], sm: spacing[20], xs: spacing[16] } }}
                                                    >
                                                        &ldquo;{review.text}&rdquo;
                                                    </Typography>
                                                </CardContent>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        paddingTop: {md: spacing[32], sm: spacing[20], xs: spacing[16]},
                                                        position: 'relative',
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            height: '1px',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.1)'
                                                        }
                                                    }}
                                                >
                                                    <Typography
                                                        variant="label5semibold"
                                                        color={color.gray[80]}
                                                    >
                                                        {review.author}
                                                    </Typography>
                                                    <Typography variant="label5regular" color={color.gray[80]}>
                                                        {review.title}
                                                    </Typography>
                                                </Box>
                                            </Card>
                                        ))}
                                    </Stack>
                                );
                            })}
                        </SlidingTrack>

                    </Box>
                    <NavigationControls
                        currentSlide={currentSlide}
                        totalSlides={totalSlides}
                        onPrev={prevSlide}
                        onNext={nextSlide}
                        goToSlide={goToSlide}
                        isDark={true}
                        nextLabel={t('nextReview')}
                        previousLabel={t('previousReview')}
                    />
                </Stack>
            </Stack>
        </SectionWrapper>
    );
}